import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { createContext, use } from "react";
import { cn } from "@/lib/utils";

type ChatBubbleTone = "inbound" | "outbound" | "system" | "informative";
type ChatMessageAlign = "start" | "end";
type ChatMessageDirection = "inbound" | "outbound";
type ChatStatusTone =
  | "default"
  | "informative"
  | "positive"
  | "warning"
  | "negative";

const ChatMessageContext = createContext<{
  align: ChatMessageAlign;
  direction?: ChatMessageDirection;
}>({ align: "start" });

const chatMessageVariants = cva(
  ["flex", "w-full", "min-w-0", "gap-(--spacing-8)"],
  {
    variants: {
      align: {
        start: "justify-start",
        end: "justify-end",
      },
    },
    defaultVariants: { align: "start" },
  },
);

const chatMessageContentStyles = cva(
  [
    "flex",
    "min-w-0",
    "w-full",
    "max-w-[520px]",
    "flex-col",
    "gap-(--spacing-4)",
  ],
  {
    variants: {
      align: {
        start: "items-start",
        end: "items-end",
      },
    },
    defaultVariants: { align: "start" },
  },
);
const chatMessageHeaderStyles = cva([
  "flex",
  "items-center",
  "gap-(--spacing-6)",
  "px-(--spacing-2)",
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "text-text-default",
]);
const chatMessageFooterStyles = cva([
  "flex",
  "items-center",
  "min-w-0",
  "w-full",
  "gap-(--spacing-8)",
]);
const chatMessageGroupStyles = cva([
  "flex",
  "w-full",
  "flex-col",
  "gap-(--spacing-8)",
]);

const chatBubbleVariants = cva(
  [
    "max-w-full",
    "break-words",
    "whitespace-pre-wrap",
    "rounded-(--radius-sm)",
    "p-(--spacing-16)",
    "shadow-elevation-1",
    "text-(length:--font-size-body-medium)",
    "leading-(--font-line-height-20)",
  ],
  {
    variants: {
      tone: {
        inbound: ["w-fit", "bg-surface-container", "text-text-default"],
        outbound: ["w-fit", "bg-surface-container", "text-text-default"],
        system: ["w-full", "bg-surface-container", "text-text-default"],
        informative: [
          "w-full",
          "bg-informative-container",
          "text-informative-on-container",
        ],
      },
    },
    defaultVariants: { tone: "inbound" },
  },
);

const chatStatusVariants = cva(
  [
    "flex",
    "w-full",
    "items-center",
    "justify-center",
    "gap-(--spacing-8)",
    "rounded-(--radius-sm)",
    "border",
    "px-(--spacing-16)",
    "py-(--spacing-8)",
    "text-center",
    "font-(family-name:--font-family-title)",
    "font-(number:--font-weight-medium)",
    "text-(length:--font-size-title-sm)",
    "leading-(--font-line-height-20)",
  ],
  {
    variants: {
      tone: {
        default: [
          "border-informative-container",
          "bg-informative-container",
          "text-informative-on-container",
        ],
        informative: [
          "border-informative-container",
          "bg-informative-container",
          "text-informative-on-container",
        ],
        positive: [
          "border-positive-on-container",
          "bg-positive-container",
          "text-positive-on-container",
        ],
        warning: [
          "border-warning-on-container",
          "bg-accessibility-warning-container",
          "text-warning-on-container",
        ],
        negative: [
          "border-negative-on-container",
          "bg-negative-container",
          "text-negative-on-container",
        ],
      },
    },
    defaultVariants: { tone: "default" },
  },
);

export interface ChatMessageProps
  extends Omit<React.ComponentPropsWithoutRef<"article">, "className">,
    Omit<VariantProps<typeof chatMessageVariants>, "align"> {
  children: React.ReactNode;
  /** Semantic message direction. Derives alignment and the default bubble tone. */
  direction?: ChatMessageDirection;
}

export interface ChatMessageAvatarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatMessageContentProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatMessageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatMessageFooterProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatMessageGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatBubbleProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className">,
    VariantProps<typeof chatBubbleVariants> {
  children: React.ReactNode;
  tone?: ChatBubbleTone;
}

export interface ChatStatusProps
  extends Omit<React.OutputHTMLAttributes<HTMLOutputElement>, "className">,
    VariantProps<typeof chatStatusVariants> {
  children: React.ReactNode;
  /** Announces status changes to assistive technology. */
  live?: "off" | "polite" | "assertive";
  tone?: ChatStatusTone;
}

export function ChatMessage({
  children,
  direction,
  ...props
}: ChatMessageProps) {
  const resolvedAlign = direction === "outbound" ? "end" : "start";

  return (
    <ChatMessageContext value={{ align: resolvedAlign, direction }}>
      <article
        data-slot="chat-message"
        data-align={resolvedAlign}
        data-direction={direction}
        className={cn(chatMessageVariants({ align: resolvedAlign }))}
        {...props}
      >
        {children}
      </article>
    </ChatMessageContext>
  );
}

export function ChatMessageAvatar({
  children,
  ...props
}: ChatMessageAvatarProps) {
  return (
    <div data-slot="chat-message-avatar" className="shrink-0" {...props}>
      {children}
    </div>
  );
}

export function ChatMessageContent({
  children,
  ...props
}: ChatMessageContentProps) {
  const { align } = use(ChatMessageContext);

  return (
    <div
      data-slot="chat-message-content"
      data-align={align}
      className={cn(chatMessageContentStyles({ align }))}
      {...props}
    >
      {children}
    </div>
  );
}

export function ChatMessageHeader({
  children,
  ...props
}: ChatMessageHeaderProps) {
  return (
    <header
      data-slot="chat-message-header"
      className={cn(chatMessageHeaderStyles())}
      {...props}
    >
      {children}
    </header>
  );
}

export function ChatMessageFooter({
  children,
  ...props
}: ChatMessageFooterProps) {
  return (
    <footer
      data-slot="chat-message-footer"
      className={cn(chatMessageFooterStyles())}
      {...props}
    >
      {children}
    </footer>
  );
}

export function ChatMessageGroup({
  children,
  ...props
}: ChatMessageGroupProps) {
  return (
    <div
      data-slot="chat-message-group"
      className={cn(chatMessageGroupStyles())}
      {...props}
    >
      {children}
    </div>
  );
}

export function ChatBubble({ children, tone, ...props }: ChatBubbleProps) {
  const { direction } = use(ChatMessageContext);
  const resolvedTone = tone ?? direction ?? "inbound";

  return (
    <div
      data-slot="chat-bubble"
      data-tone={resolvedTone}
      className={cn(chatBubbleVariants({ tone: resolvedTone }))}
      {...props}
    >
      {children}
    </div>
  );
}

export function ChatStatus({
  children,
  live = "polite",
  tone = "default",
  ...props
}: ChatStatusProps) {
  return (
    <output
      data-slot="chat-status"
      data-tone={tone}
      aria-live={live}
      className={cn(chatStatusVariants({ tone }))}
      {...props}
    >
      {children}
    </output>
  );
}

export type { ChatBubbleTone, ChatMessageDirection, ChatStatusTone };
